import { useMemo, useCallback, useState, useEffect } from 'react';
import { loadPersonalisationFonts } from '../../utilities/fontLoader';
import { useLocale } from '../../context/LocaleContext';
import { getRegexFromRestrictions } from '../../utilities/getRegexFromRestrictions';

/**
 * Custom hook for managing alphanumeric slot view model.
 * @param {Object} slot - The slot object containing alphanumeric configuration.
 * @param {Function} onChange - Callback function for handling value changes.
 * @returns {Object} View model for the alphanumeric slot component.
 */
export function useAlphanumericSlotViewModel(slot, onChange, inputValue = '') {
  const { formatPrice } = useLocale();

  // inputValue may be a string or { text, font, translatedText } object — cast to avoid
  // TypeScript narrowing it to never when we access object properties below.
  const inputValueObj = /** @type {{ text?: string, font?: string, translatedText?: string } | null} */ (
    inputValue && typeof inputValue === 'object' ? inputValue : null
  );

  // State for tracking the currently selected font.
  // Initialise from inputValue.font so that a remount (e.g. after a grouped option switch)
  // restores the font the user already chose rather than falling back to the default.
  const [selectedFont, setSelectedFont] = useState(() => {
    return inputValueObj?.font || '';
  });

  // State for tracking the current text input.
  // Initialise from inputValue so that a remount (e.g. after a grouped option switch)
  // preserves the text already in context — prevents font changes from clearing the field.
  const [currentText, setCurrentText] = useState(() => {
    if (inputValueObj?.text) return inputValueObj.text;
    if (typeof inputValue === 'string') return inputValue;
    return '';
  });

  // State for tracking the translated text.
  const [translatedText, setTranslatedText] = useState('');

  // State for tracking translation loading state.
  const [isTranslating, setIsTranslating] = useState(false);

  // Check if translation is enabled for this slot.
  const hasTranslation = Boolean(slot?.translateToLanguage);
  const targetLanguage = slot?.translateToLanguage || '';

  // Parse allowedFonts from string to array.
  const parsedAllowedFonts = useMemo(() => {
    // Early return if no allowedFonts.
    if (!slot?.allowedFonts) return [];

    if (Array.isArray(slot.allowedFonts)) {
      return slot.allowedFonts;
    }

    if (typeof slot.allowedFonts === 'string') {
      try {
        return JSON.parse(slot.allowedFonts);
      } catch {
        return [];
      }
    }

    return [];
  }, [slot?.allowedFonts]);

  // Initialise selected font once fonts are resolved.
  // Priority: 1) font already in selection (inputValue.font), 2) admin default, 3) first available.
  // This effect intentionally does NOT include inputValue in its deps — after mount the user's
  // explicit font choice (handleFontChange) is the source of truth; we only want this to run when
  // the font list itself changes (e.g. slot config update).
  useEffect(() => {
    const fontFromSelection = inputValueObj?.font ?? null;

    if (fontFromSelection && parsedAllowedFonts.includes(fontFromSelection)) {
      setSelectedFont(fontFromSelection);
    } else if (slot?.defaultFont && parsedAllowedFonts.includes(slot.defaultFont)) {
      setSelectedFont(slot.defaultFont);
    } else if (parsedAllowedFonts.length > 0) {
      setSelectedFont(parsedAllowedFonts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.defaultFont, parsedAllowedFonts]);

  // Inject fonts when component mounts and fonts are available
  useEffect(() => {
    if (parsedAllowedFonts.length > 0) {
      loadPersonalisationFonts(parsedAllowedFonts);
    }

    // Cleanup function to remove injected font styles when component unmounts
    return () => {
      const existingStyle = document.getElementById('personalisation-fonts');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [parsedAllowedFonts]);

  /**
   * Translates text using Google Translate API.
   * @param {string} text - Text to translate.
   * @param {string} targetLang - Target language code.
   * @returns {Promise<string>} Translated text.
   */
  const translateText = useCallback(async (text, targetLang) => {
    if (!text || !targetLang) {
      return '';
    }

    setIsTranslating(true);

    try {
      const response = await fetch(
        `https://translation.googleapis.com/language/translate/v2?key=${window.googleTranslateApiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            q: text,
            target: targetLang,
            format: 'text'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Translation failed: ${response.status}`);
      }

      const data = await response.json();

      const translatedText = data.data?.translations?.[0]?.translatedText || '';

      return translatedText;
    } catch {
      return text; // Fallback to original text on error
    } finally {
      setIsTranslating(false);
    }
  }, []);

  // Effect to trigger translation when text changes.
  useEffect(() => {
    if (hasTranslation && currentText && targetLanguage) {
      const timeoutId = setTimeout(() => {
        translateText(currentText, targetLanguage).then((result) => {
          setTranslatedText(result);

          // Call onChange with the updated translation
          if (hasTranslation && slot?.id) {
            onChange(slot.id, {
              text: currentText,
              translatedText: result,
              font: selectedFont
            });
          }
        });
      }, 500); // Debounce translation requests.

      return () => clearTimeout(timeoutId);
    } else if (!currentText) {
      setTranslatedText('');
    }
    // Intentionally excluding onChange, selectedFont, and slot.id as causing infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentText, hasTranslation, targetLanguage, translateText]);

  // Convert boolean-like values to actual booleans.
  const isRequiredField = slot?.isRequired === true || slot?.isRequired === 'true';
  const isAllowedSpaces = slot?.allowSpaces === true || slot?.allowSpaces === 'true';
  const isAllowedLetters = slot?.allowLetters === true || slot?.allowLetters === 'true';
  const isAllowedNumbers = slot?.allowNumbers === true || slot?.allowNumbers === 'true';
  const isAllowedSymbols = slot?.allowSymbols === true || slot?.allowSymbols === 'true';

  /**
   * Builds a restriction message based on the current sanitization settings.
   * @returns {string} The restriction message.
   */
  const inputRestrictionMessage = useMemo(() => {
    const restrictions = [];

    if (!isAllowedSpaces) {
      restrictions.push('spaces');
    }

    if (!isAllowedLetters) {
      restrictions.push('letters');
    }

    if (!isAllowedNumbers) {
      restrictions.push('numbers');
    }

    if (!isAllowedSymbols) {
      restrictions.push('symbols');
    }

    if (restrictions.length === 0) {
      return '';
    }

    if (restrictions.length === 1) {
      return `Please avoid using ${restrictions[0]}`;
    }

    if (restrictions.length === 2) {
      return `Please avoid using ${restrictions[0]} and ${restrictions[1]}`;
    }

    return `Please avoid using ${restrictions.slice(0, -1).join(', ')}, and ${restrictions[restrictions.length - 1]}`;
  }, [isAllowedSpaces, isAllowedLetters, isAllowedNumbers, isAllowedSymbols]);

  /**
   * Transforms text according to the specified text case.
   * @param {string} text - The text to transform.
   * @param {string} textCase - The target text case (UPPER, LOWER, SENTENCE, MIXED).
   * @returns {string} The transformed text.
   */
  const transformTextCase = (text, textCase) => {
    if (!text) return '';

    switch (textCase) {
      case 'UPPER':
        return text.toUpperCase();
      case 'LOWER':
        return text.toLowerCase();
      case 'SENTENCE':
        // Split by spaces, transform each word, and join back
        return text
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      case 'MIXED':
      default:
        return text;
    }
  };

  const handleInputChange = useCallback(
    (id, newValue) => {
      // First, apply text case transformation.
      let sanitizedValue = transformTextCase(newValue, slot?.allowedTextCase || 'MIXED');

      // Create restrictions object for the regex function.
      const restrictions = {
        allowNumbers: isAllowedNumbers,
        allowLetters: isAllowedLetters,
        allowSpaces: isAllowedSpaces,
        allowSymbols: isAllowedSymbols,
        allowedTextCase: slot?.allowedTextCase || 'MIXED'
      };

      // Get the regex pattern for allowed characters.
      const allowedPattern = getRegexFromRestrictions(restrictions);

      // Create regex to match only allowed characters.
      // eslint-disable-next-line
      const allowedRegex = new RegExp(`[${allowedPattern}]`, 'g');

      // Filter the input to only keep allowed characters.
      const matches = sanitizedValue.match(allowedRegex);
      sanitizedValue = matches ? matches.join('') : '';

      // CRITICAL: Enforce maxLength as JavaScript safety net AFTER all transformations.
      // Some browsers/keyboards (Samsung, IME, autofill, paste, voice input) can bypass
      // the native HTML maxLength attribute.
      // Parse as integer to handle both string and number values from admin config.
      const maxLen = parseInt(slot?.maxLength, 10);
      if (!isNaN(maxLen) && maxLen > 0 && sanitizedValue.length > maxLen) {
        sanitizedValue = sanitizedValue.slice(0, maxLen);
      }

      // Update local text state.
      setCurrentText(sanitizedValue);

      // Call onChange with both text and current font selection.
      // For translation slots, use the translated text as the final value.
      const finalValue = hasTranslation ? translatedText : sanitizedValue;

      onChange(id, {
        text: sanitizedValue,
        translatedText: finalValue,
        font: selectedFont
      });
    },
    [
      isAllowedSpaces,
      isAllowedLetters,
      isAllowedNumbers,
      isAllowedSymbols,
      slot?.allowedTextCase,
      slot?.maxLength,
      onChange,
      selectedFont,
      hasTranslation,
      translatedText
    ]
  );

  /**
   * Handles key down events to prevent restricted characters from being typed.
   * Also enforces maxLength to prevent input from exceeding the limit.
   * @param {KeyboardEvent} e - The keyboard event.
   * @returns {boolean} True if the key should be prevented.
   */
  const handleKeyDown = useCallback(
    (e) => {
      // Always allow control and modifier keys to work normally.
      const isControlKey =
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.key === 'Escape' ||
        e.metaKey || // Cmd key on Mac.
        e.ctrlKey || // Ctrl key on Windows/Linux.
        e.altKey; // Alt key.

      if (isControlKey) {
        return false; // Allow special keys to work normally.
      }

      // Check maxLength before accepting any character input.
      // Parse maxLength as integer to handle both string and number values.
      const maxLen = parseInt(slot?.maxLength, 10);
      if (!isNaN(maxLen) && maxLen > 0) {
        const input = e.target;
        const currentValue = input.value || '';
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;
        const hasSelection = selectionStart !== selectionEnd;

        // If there's no selection and we're at max length, prevent input.
        // If there's a selection, allow input as it will replace the selected text.
        if (!hasSelection && currentValue.length >= maxLen) {
          e.preventDefault();
          return true; // Prevent character input when at max length.
        }
      }

      // Only apply restrictions to regular character keys.
      if (!isAllowedSpaces && e.key === ' ') {
        e.preventDefault();
        return true; // Prevent space.
      }

      // Prevents letters from being typed if allowLetters is false.
      if (!isAllowedLetters) {
        const isLetter = /[a-zA-Z]/.test(e.key);
        if (isLetter) {
          e.preventDefault();
          return true; // Prevent letter.
        }
      }

      // Prevents numbers from being typed if allowNumbers is false.
      if (!isAllowedNumbers) {
        const isNumber = /[0-9]/.test(e.key);
        if (isNumber) {
          e.preventDefault();
          return true; // Prevent number.
        }
      }

      // Prevents symbols from being typed if allowSymbols is false.
      if (!isAllowedSymbols) {
        // Check if the key is a symbol (not alphanumeric).
        // But allow spaces if they are permitted.
        let isSymbol = /[^a-zA-Z0-9]/.test(e.key);

        // If spaces are allowed, don't treat space as a symbol.
        if (isAllowedSpaces && e.key === ' ') {
          isSymbol = false;
        }

        if (isSymbol) {
          e.preventDefault();
          return true; // Prevent symbol.
        }
      }

      return false; // Allow the key.
    },
    [isAllowedSpaces, isAllowedLetters, isAllowedNumbers, isAllowedSymbols, slot?.maxLength]
  );

  /**
   * Handles paste events to enforce maxLength and character restrictions on pasted content.
   * This is necessary because paste can bypass the native HTML maxLength attribute.
   * @param {ClipboardEvent} e - The paste event.
   * @param {string} id - Slot ID.
   * @param {string} currentValue - Current input value.
   */
  const handlePaste = useCallback(
    (e, id, currentValue) => {
      e.preventDefault();

      const pastedText = e.clipboardData?.getData('text') || '';
      if (!pastedText) return;

      // Get cursor position info for proper insertion.
      const input = e.target;
      const selectionStart = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || 0;

      // Build the new value with pasted text inserted at cursor position.
      const beforeSelection = currentValue.slice(0, selectionStart);
      const afterSelection = currentValue.slice(selectionEnd);
      const newValue = beforeSelection + pastedText + afterSelection;

      // Let handleInputChange sanitize and enforce maxLength.
      handleInputChange(id, newValue);
    },
    [handleInputChange]
  );

  const isValidFont = useCallback(
    (fontName) => {
      return parsedAllowedFonts.includes(fontName);
    },
    [parsedAllowedFonts]
  );

  /**
   * Handles font selection change.
   * @param {string} fontName - The selected font name.
   */
  const handleFontChange = useCallback(
    (fontName) => {
      // Validate that the font is in the allowed list
      if (!isValidFont(fontName)) {
        console.warn(`Font "${fontName}" is not in the allowed fonts list:`, parsedAllowedFonts);
        return;
      }

      setSelectedFont(fontName);

      // Call onChange with current text and new font selection.
      const finalValue = hasTranslation ? translatedText : currentText;

      onChange(slot?.id, {
        text: currentText, // Keep the current text value
        translatedText: finalValue,
        font: fontName
      });
    },
    [slot?.id, onChange, currentText, isValidFont, parsedAllowedFonts, hasTranslation, translatedText]
  );

  return {
    // Slot properties.
    id: slot?.id || '',
    inputLabel: slot?.inputLabel || '',
    isRequired: isRequiredField,
    placeholderText: slot?.placeholderText || 'Enter text',
    maxLength: slot?.maxLength,
    isDisabled: slot?.disabled,

    // Input restrictions.
    allowSpaces: isAllowedSpaces,
    allowLetters: isAllowedLetters,
    allowNumbers: isAllowedNumbers,
    allowSymbols: isAllowedSymbols,
    allowedTextCase: slot?.allowedTextCase || '',

    // Font options.
    allowedFonts: slot?.allowedFonts || [],
    parsedAllowedFonts,
    defaultFont: slot?.defaultFont || '',
    selectedFont,

    // Translation options.
    hasTranslation,
    targetLanguage,
    translatedText,
    isTranslating,

    // Validation helpers.
    hasFontOptions: parsedAllowedFonts.length > 0,
    inputRestrictionMessage,
    hasRestrictions: !isAllowedSpaces || !isAllowedLetters || !isAllowedNumbers || !isAllowedSymbols,

    // Event handlers.
    handleInputChange,
    handleKeyDown,
    handlePaste,
    handleFontChange,
    formatPrice
  };
}
